import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { ColorPicker } from '../components/ui/ColorPicker';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Sheet } from '../components/ui/Sheet';
import { Toggle } from '../components/ui/Toggle';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';
import { SmartBannerForm } from '../components/SmartBannerForm';

interface SmartBanner {
  id: number;
  establishment_id: number;
  name: string;
  background_type: 'color' | 'image';
  background_color: string | null;
  background_image_path: string | null;
  header_type: 'image' | 'text';
  header_image_path: string | null;
  header_text: string | null;
  header_font_id: number | null;
  header_font_size: number;
  header_text_color: string | null;
  header_text_align: 'left' | 'center' | 'right';
  columns: number;
  is_active: number;
  display_order: number;
  items_count: number;
  screens_count: number;
  created_at: string;
  updated_at: string;
}

interface Establishment {
  id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  is_active: number;
}

export const SmartBanners: React.FC = () => {
  const { token } = useAuth();
  const [smartBanners, setSmartBanners] = useState<SmartBanner[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SmartBanner | null>(null);

  // Cargar establecimientos
  useEffect(() => {
    const loadEstablishments = async () => {
      if (!token) {
        setLoadingEstablishments(false);
        return;
      }

      setLoadingEstablishments(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '1000'
        });

        const route = `establishments/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          setError('Error de conexión al cargar establecimientos');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
          throw new Error(errorMessage);
        }

        setEstablishments(json.data?.establishments || []);
      } catch (err: any) {
        console.error('Error cargando establecimientos:', err);
        setError(err.message || 'Error al cargar establecimientos');
      } finally {
        setLoadingEstablishments(false);
      }
    };

    loadEstablishments();
  }, [token]);

  // Cargar smart banners cuando se selecciona un establecimiento
  useEffect(() => {
    if (selectedEstablishmentId && token) {
      loadSmartBanners();
    } else {
      setSmartBanners([]);
    }
  }, [selectedEstablishmentId, token]);

  const loadSmartBanners = async () => {
    if (!token || !selectedEstablishmentId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId
      });

      const route = `smart-banners/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar banners inteligentes');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar banners inteligentes';
        toast.error(errorMessage);
        setSmartBanners([]);
        return;
      }

      const responseData = json.data || json;
      const banners = responseData.smart_banners || [];
      
      // Aplicar búsqueda si existe
      let filteredBanners = banners;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredBanners = banners.filter((b: SmartBanner) =>
          b.name.toLowerCase().includes(searchLower)
        );
      }
      
      setSmartBanners(filteredBanners);
    } catch (err: any) {
      console.error('Error cargando banners inteligentes:', err);
      toast.error(err.message || 'Error al cargar banners inteligentes');
      setSmartBanners([]);
    } finally {
      setLoading(false);
    }
  };

  // Recargar cuando cambia la búsqueda
  useEffect(() => {
    if (selectedEstablishmentId) {
      loadSmartBanners();
    }
  }, [search]);

  const handleEdit = async (banner: SmartBanner) => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        id: banner.id.toString()
      });

      const route = `smart-banners/get&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar banner');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar banner';
        toast.error(errorMessage);
        return;
      }

      const responseData = json.data || json;
      const fullBanner = responseData.smart_banner;
      
      setEditingBanner(fullBanner);
      setShowForm(true);
    } catch (err: any) {
      console.error('Error cargando banner:', err);
      toast.error(err.message || 'Error al cargar banner');
    }
  };

  const handleDelete = async (bannerId: number) => {
    if (!token) return;
    if (!window.confirm('¿Estás seguro de que deseas eliminar este banner inteligente?')) return;

    try {
      const res = await apiFetch(
        'smart-banners/delete',
        {
          method: 'DELETE',
          body: JSON.stringify({ id: bannerId })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        let errorMessage = 'Error al eliminar banner inteligente';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        toast.error(errorMessage);
      } else {
        toast.success('Banner inteligente eliminado exitosamente');
        loadSmartBanners();
      }
    } catch (error: any) {
      console.error('Error al eliminar banner inteligente:', error);
      toast.error('Error al eliminar banner inteligente');
    }
  };

  const handleCreate = () => {
    if (!selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento primero');
      return;
    }
    setEditingBanner(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingBanner(null);
    loadSmartBanners();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBanner(null);
  };

  const handleSearch = () => {
    if (selectedEstablishmentId) {
      loadSmartBanners();
    }
  };

  // Filtrar banners por búsqueda
  const filteredBanners = search
    ? smartBanners.filter(b =>
        b.name.toLowerCase().includes(search.toLowerCase())
      )
    : smartBanners;

  return (
    <DashboardLayout
      title="Banners Inteligentes"
      subtitle="Administra los banners inteligentes configurables"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingBanner ? 'Editar Banner Inteligente' : 'Nuevo Banner Inteligente'}
        subtitle={editingBanner ? 'Modifica la configuración del banner' : 'Crea un nuevo banner inteligente configurable'}
        size="xl"
      >
        <SmartBannerForm
          banner={editingBanner}
          establishmentId={selectedEstablishmentId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Sheet>

      <div className="space-y-6">
        {/* Selector de Establecimiento */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <Select
                  label="Seleccionar Establecimiento"
                  value={selectedEstablishmentId}
                  onChange={(e) => setSelectedEstablishmentId(e.target.value)}
                  options={[
                    { value: '', label: '-- Selecciona un establecimiento --' },
                    ...establishments.filter(e => e.is_active === 1).map(e => ({
                      value: e.id.toString(),
                      label: `${e.name_establishment} (Cód: ${e.code_establishment_hioffice})`
                    }))
                  ]}
                  disabled={loadingEstablishments}
                />
              </div>
              {selectedEstablishmentId && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCreate}
                  className="whitespace-nowrap"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Agregar Banner Inteligente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {!selectedEstablishmentId ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Selecciona un Establecimiento</h3>
                <p className="text-gray-600">
                  Elige un establecimiento del menú desplegable para ver y gestionar sus banners inteligentes.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Barra de Búsqueda */}
            {smartBanners.length > 0 && (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    onSubmit={handleSearch}
                    placeholder="Buscar por nombre de banner..."
                    className="w-full"
                  />
                </CardContent>
              </Card>
            )}

            {/* Lista de Smart Banners */}
            {loading ? (
              <Card>
                <CardContent>
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                    <p className="text-gray-600">Cargando banners inteligentes...</p>
                  </div>
                </CardContent>
              </Card>
            ) : filteredBanners.length === 0 ? (
              <Card>
                <CardContent>
                  <div className="p-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="font-medium mb-2">No hay banners inteligentes configurados</p>
                    <p className="text-sm">
                      {search
                        ? 'No se encontraron banners con los criterios de búsqueda.'
                        : 'Haz clic en "Agregar Banner Inteligente" para crear uno nuevo'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBanners.map((banner) => (
                  <Card key={banner.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1 truncate">
                            {banner.name}
                          </h4>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            <span>Items: {banner.items_count}</span>
                            <span>•</span>
                            <span>Pantallas: {banner.screens_count}</span>
                            <span>•</span>
                            <span>Columnas: {banner.columns}</span>
                          </div>
                        </div>
                        {banner.is_active === 1 ? (
                          <Badge variant="success" className="ml-2 flex-shrink-0">
                            Activo
                          </Badge>
                        ) : (
                          <Badge variant="neutral" className="ml-2 flex-shrink-0">
                            Inactivo
                          </Badge>
                        )}
                      </div>

                      <div className="mb-3 space-y-1 text-xs text-gray-400">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Fondo:</span>
                          <span>{banner.background_type === 'color' ? 'Color' : 'Imagen'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Header:</span>
                          <span>{banner.header_type === 'image' ? 'Imagen' : 'Texto'}</span>
                        </div>
                      </div>


                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(banner)}
                          className="flex-1"
                        >
                          Editar
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDelete(banner.id)}
                          className="flex-1"
                        >
                          Eliminar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

